import React from "react"
import { createContext, useState, useCallback, useContext } from "react"
import moment from "moment-timezone"
import _ from "lodash"
import { RelatorioTransacoes, Transacoes } from "@src/services/functions/requestFunctions"

const FormattedDocument = (doc) => doc
const SituacaoTexto = (status) => status
const FormaPagamento = ({ formapag }) => formapag

const ReportContext = createContext(null)

export const ReportProvider = ({ children }) => {
  const [isGenerating, setIsGenerating] = useState(false)
  const [currentPageProgress, setCurrentPageProgress] = useState(0)
  const [totalItemsFetched, setTotalItemsFetched] = useState(0)
  const [generatedReportData, setGeneratedReportData] = useState(null)
  const [reportError, setReportError] = useState(null)
  const [reportType, setReportType] = useState(null)
  const [isMinimized, setIsMinimized] = useState(false)

  const processData = useCallback((allData, type) => {
    if (allData.length === 0) return []

    if (type === "detalhado") {
      const dados = allData.map((item) => {
        return {
          Código: item.merchant_id,
          Estabelecimento: item.merchant_name,
          Documento: FormattedDocument(item.cpfCnpj),
          Origem: item.original_merchants_name ?? "-",
          Bandeira: item.card_brand,
          "NSU Trans.": item.nsu_transacao,
          "NSU Parc.": item.payables_id,
          "Valor Venda": (item.valor_bruto / 100)
            .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
            .replace(/[^0-9.,]+/g, ""),
          Parcela: item.parcela_atual,
          "Total Parcelas": item.total_parcela,
          "Valor Bruto": (item.valor_bruto_parcela / 100)
            .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
            .replace(/[^0-9.,]+/g, ""),
          "Valor Líquido": (item.valor_liquido_parcela / 100)
            .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
            .replace(/[^0-9.,]+/g, ""),
          "MDR Bruto": (item.custo_mdr / 100)
            .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
            .replace(/[^0-9.,]+/g, ""),
          "Líquido EC": (item.receita_antecipacao / 100)
            .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
            .replace(/[^0-9.,]+/g, ""),
          Terminal: `[${item.capture_method.toUpperCase()}] ${item.device_serial_number}`,
          "Data Venda": moment(item.start_date).subtract(3, "hours").format("DD/MM/YYYY HH:mm:ss"),
          "Data Pagamento": item.payment_date
            ? moment(item.payment_date).subtract(3, "hours").format("DD/MM/YYYY")
            : "",
          Situação: SituacaoTexto(item.situacao),
        }
      })
      return _.orderBy(dados, ["Código"], ["asc"])
    } else if (type === "agrupado") {
      const dados = allData.map((item) => {
        return {
          "Código EC": item.merchant_id,
          EC: `[ ${FormattedDocument(item.merchant_document_number)} ] ${item.merchant_name}`,
          Origem: item.original_merchants_name ?? "-",
          "ID Venda": item.id,
          "Data Venda": moment(item.start_date).subtract(3, "hours").format("YYYY-MM-DD HH:mm:ss"),
          Bandeira: item.card_brand,
          "NSU (DOC/CV)": item.nsu ?? "-",
          "Código Autorizacao": item.authorization_code ?? "-",
          Parcelas: item.installments,
          Modalidade: FormaPagamento({ formapag: item.payment_method }),
          "Valor Venda": (item.amount / 100)
            .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
            .replace(/[^0-9.,]+/g, ""),
          MDR: (item.custo_mdr / 100)
            .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
            .replace(/[^0-9.,]+/g, ""),
          Antecipação: (item.anticipation_fee / 100)
            .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
            .replace(/[^0-9.,]+/g, ""),
          "Valor Líquido": (item.valor_liquido / 100)
            .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
            .replace(/[^0-9.,]+/g, ""),
          "Tipo Terminal": item?.capture_method?.split("_")[0]?.toUpperCase(),
          Terminal: item?.device_serial_number,
          "Data Confirmação Venda": item.finish_date
            ? moment(item.finish_date).subtract(3, "hours").format("YYYY-MM-DD HH:mm:ss")
            : "",
          Situacao: SituacaoTexto(item.status),
        }
      })
      return _.orderBy(dados, ["id", "asc"])
    }
    return []
  }, [])

  const startReportGeneration = useCallback(
    async (params, type) => {
      setIsGenerating(true)
      setReportError(null)
      setGeneratedReportData(null)
      setReportType(type)
      setCurrentPageProgress(0)
      setTotalItemsFetched(0)
      setIsMinimized(false)

      let allData = []
      let page = 1
      let hasMore = true
      const limit = 2000

      try {
        if (type === "agrupado") {
          const response = await Transacoes({ ...params, download: 1 }) 
          if (response && response.status === 200 && response.data && response.data.data) {
            allData = response.data.data
          } else {
            throw new Error("Erro ao buscar dados agrupados.")
          }
          hasMore = false 
        } else {
          while (hasMore) {
            setCurrentPageProgress(page)
            const response = await RelatorioTransacoes({ ...params, page, limit })

            if (response && response.status === 200) {
              const currentData = Array.isArray(response.data) ? response.data : response.data.data || []
              allData = [...allData, ...currentData]
              setTotalItemsFetched((prev) => prev + currentData.length)
              hasMore = response.data.hasMore || currentData.length === limit
              page++
            } else {
              hasMore = false
              setReportError("Erro ao buscar dados paginados: " + (response?.mensagem || "Erro desconhecido"))
              console.error("Erro ao buscar dados paginados:", response)
            }
          }
        }

        const processedData = processData(allData, type)
        setGeneratedReportData(processedData)
      } catch (error) {
        setReportError("Erro na geração do relatório: " + error.message)
        console.error("Erro na geração do relatório:", error)
      } finally {
        setIsGenerating(false)
        setIsMinimized(true)
      }
    },
    [processData],
  )

  const clearGeneratedReport = useCallback(() => {
    setGeneratedReportData(null)
    setReportError(null)
    setReportType(null)
    setCurrentPageProgress(0)
    setTotalItemsFetched(0)
    setIsMinimized(false)
  }, [])

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev)
  }, [])

  return (
    <ReportContext.Provider
      value={{
        isGenerating,
        currentPageProgress,
        totalItemsFetched,
        generatedReportData,
        reportError,
        reportType,
        isMinimized,
        startReportGeneration,
        clearGeneratedReport,
        toggleMinimize, 
      }}
    >
      {children}
    </ReportContext.Provider>
  )
}

export const useReport = () => {
  const context = useContext(ReportContext)
  if (context === undefined) {
    throw new Error("useReport must be used within a ReportProvider")
  }
  return context
}
